// Buyer Verification - Multi-step signup form with dynamic country fields
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { 
  Globe, Mail, Phone, Building2, Linkedin, User, MapPin, 
  CheckCircle, XCircle, Loader2, ArrowRight, ArrowLeft,
  AlertTriangle, Shield, Upload, HelpCircle, ExternalLink,
  Briefcase, DollarSign, Calendar, Package, CreditCard, Video
} from 'lucide-react';
import { toast } from 'sonner';
import RegistrationVideoVerification from '@/components/RegistrationVideoVerification';

const API = window.location.origin;

// Buyer roles
const BUYER_ROLES = [
  { value: 'owner', label: 'Owner / Director' },
  { value: 'purchase_manager', label: 'Purchase Manager' },
  { value: 'trader', label: 'Trader / Agent' },
  { value: 'other', label: 'Other' }
];

// Monthly volume options
const VOLUME_OPTIONS = [
  { value: '<$10k', label: 'Less than $10,000' },
  { value: '$10k–50k', label: '$10,000 - $50,000' },
  { value: '$50k–200k', label: '$50,000 - $200,000' },
  { value: '$200k+', label: 'More than $200,000' }
];

// Buying frequency options
const FREQUENCY_OPTIONS = [
  { value: 'one_time', label: 'One-time purchase' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'ongoing', label: 'Ongoing / Regular' }
];

// Payment terms
const PAYMENT_TERMS = [
  { value: 'lc', label: 'Letter of Credit (LC)' },
  { value: 'tt', label: 'Telegraphic Transfer (TT)' },
  { value: 'dp', label: 'Documents against Payment (DP)' },
  { value: 'open_account', label: 'Open Account' }
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediate (within 2 weeks)' },
  { value: '1_month', label: 'Within 1 month' },
  { value: '3_months', label: 'Within 3 months' },
  { value: 'exploratory', label: 'Just exploring' }
];

// Product categories (simplified - in production, fetch from API)
const PRODUCT_CATEGORIES = [
  { id: 'spices', label: 'Spices & Seasonings' },
  { id: 'textiles', label: 'Textiles & Fabrics' },
  { id: 'handicrafts', label: 'Handicrafts & Decor' },
  { id: 'leather', label: 'Leather Goods' },
  { id: 'gems', label: 'Gems & Jewelry' },
  { id: 'chemicals', label: 'Chemicals' },
  { id: 'pharma', label: 'Pharmaceuticals' },
  { id: 'engineering', label: 'Engineering Goods' },
  { id: 'food', label: 'Food & Beverages' },
  { id: 'agriculture', label: 'Agricultural Products' },
  { id: 'other', label: 'Other' }
];

export default function BuyerSignup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryFields, setCountryFields] = useState([]);
  const [verificationId, setVerificationId] = useState(null);
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Universal fields
    full_name: '',
    email: '',
    country_code: '',
    city: '',
    company_name: '',
    company_website: '',
    phone: '',
    linkedin_profile_url: '',
    linkedin_company_url: '',
    role: '',
    // Step 2: Country-specific fields (dynamic)
    country_fields: {},
    // Step 3: Buying intent
    products_interested: [],
    monthly_volume: '',
    target_countries: ['IN'], // Default to India
    buying_frequency: '',
    payment_terms: '',
    sample_required: false,
    timeline: '',
    current_suppliers: ''
  });
  
  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Video verification
  const [videoBlob, setVideoBlob] = useState(null);

  // Verification status
  const [verificationStatus, setVerificationStatus] = useState({
    email_checked: false,
    country_submitted: false,
    video_recorded: false,
    intent_submitted: false
  });

  // Load countries on mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch country fields when country changes
  useEffect(() => {
    if (formData.country_code) {
      fetchCountryFields(formData.country_code);
    }
  }, [formData.country_code]);

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API}/api/buyer-verification/countries`);
      setCountries(res.data.countries || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchCountryFields = async (countryCode) => {
    try {
      const res = await axios.get(`${API}/api/buyer-verification/country-fields/${countryCode}`);
      setCountryFields(res.data.fields || []);
    } catch (error) {
      console.error('Failed to fetch country fields:', error);
    }
  };

  const checkEmail = async () => {
    if (!formData.email) return;
    
    try {
      const res = await axios.post(`${API}/api/buyer-verification/check-email`, {
        email: formData.email
      });
      setEmailCheckResult(res.data);
      setVerificationStatus(prev => ({ ...prev, email_checked: true }));
      
      if (res.data.email_type === 'disposable') {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check email');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Check email when it changes
    if (field === 'email' && value.includes('@')) {
      // Debounce email check
      setTimeout(() => checkEmail(), 500);
    }
  };

  const handleCountryFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      country_fields: { ...prev.country_fields, [fieldId]: value }
    }));
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => {
      const current = prev.products_interested;
      if (current.includes(productId)) {
        return { ...prev, products_interested: current.filter(p => p !== productId) };
      } else {
        return { ...prev, products_interested: [...current, productId] };
      }
    });
  };

  // Step 1: Submit universal fields - then go directly to Step 2 (Business ID)
  const submitStep1 = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/buyer-verification/start`, {
        full_name: formData.full_name,
        email: formData.email,
        country_code: formData.country_code,
        city: formData.city,
        company_name: formData.company_name,
        company_website: formData.company_website,
        phone: formData.phone,
        linkedin_profile_url: formData.linkedin_profile_url,
        linkedin_company_url: formData.linkedin_company_url,
        role: formData.role,
        terms_accepted: termsAccepted,
        terms_accepted_at: new Date().toISOString()
      });
      
      setVerificationId(res.data.verification_id);
      setEmailCheckResult({
        email_type: res.data.email_type,
        message: res.data.email_message,
        requirements: res.data.email_requirements
      });
      setCountryFields(res.data.country_fields || []);
      
      toast.success('Great! Now complete your business verification.');
      setCurrentStep(2); // Go directly to business ID (skip OTP)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start verification');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit country/business fields (was Step 3)
  const submitStep2 = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/buyer-verification/submit-country-fields`, {
        verification_id: verificationId,
        country_code: formData.country_code,
        fields: formData.country_fields
      });
      
      setVerificationStatus(prev => ({ ...prev, country_submitted: true }));
      
      if (res.data.needs_admin_review) {
        toast.info('Business details submitted for review.');
      } else {
        toast.success('Business verified automatically!');
      }
      
      setCurrentStep(3); // Go to buying intent
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Video verification - just update state (video is captured by component)
  const submitStep3 = () => {
    if (!videoBlob) {
      toast.error('Please record your video verification');
      return;
    }
    setVerificationStatus(prev => ({ ...prev, video_recorded: true }));
    setCurrentStep(4);
  };

  // Step 4: Submit buying intent (was Step 3)
  const submitStep4 = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/buyer-verification/submit-buying-intent`, {
        verification_id: verificationId,
        products_interested: formData.products_interested,
        monthly_volume: formData.monthly_volume,
        target_countries: formData.target_countries,
        buying_frequency: formData.buying_frequency,
        payment_terms: formData.payment_terms,
        sample_required: formData.sample_required,
        timeline: formData.timeline,
        current_suppliers: formData.current_suppliers || null
      });
      
      setVerificationStatus(prev => ({ ...prev, intent_submitted: true }));
      
      if (res.data.status === 'approved') {
        toast.success('Congratulations! You are now a Verified Buyer.');
        setTimeout(() => navigate('/marketplace'), 2000);
      } else {
        toast.success('Application submitted! We\'ll review and notify you within 24 hours.');
        setCurrentStep(6); // Success page (now step 6)
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  // Handle video recorded callback
  const handleVideoRecorded = (blob) => {
    setVideoBlob(blob);
    setVerificationStatus(prev => ({ ...prev, video_recorded: true }));
  };

  // Get primary business ID label for current country
  const getPrimaryBusinessIdLabel = () => {
    const primaryField = countryFields.find(f => f.required);
    return primaryField?.label || 'Business Registration Number';
  };

  const canProceedStep1 = formData.full_name && formData.email && formData.country_code && 
    formData.city && formData.company_name && formData.phone && formData.role &&
    emailCheckResult?.email_type !== 'disposable';

  // Step 2 can proceed if required country fields are filled
  const canProceedStep2 = countryFields.filter(f => f.required).every(f => 
    formData.country_fields[f.id]
  );

  // Step 3 can proceed if video is recorded
  const canProceedStep3 = !!videoBlob;

  // Step 4 can proceed if buying intent is complete
  const canProceedStep4 = formData.products_interested.length > 0 && 
    formData.monthly_volume && formData.buying_frequency && 
    formData.payment_terms && formData.timeline;

  const getStepProgress = () => {
    return ((currentStep - 1) / 4) * 100; // 4 steps now
  };

  const renderEmailBadge = () => {
    if (!emailCheckResult) return null;
    
    const { email_type } = emailCheckResult;
    
    if (email_type === 'business') {
      return (
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Business Email - Instant Verification
        </Badge>
      );
    } else if (email_type === 'free') {
      return (
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Free Email - Document Upload Required
        </Badge>
      );
    } else if (email_type === 'disposable') {
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
          <XCircle className="h-3 w-3 mr-1" />
          Disposable Email - Not Allowed
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Globe className="h-3 w-3 mr-1" />
            Buyer Verification
          </Badge>
          <h1 className="text-3xl font-bold text-white mb-2">
            Become a Verified Buyer
          </h1>
          <p className="text-slate-400">
            Get access to contact verified exporters and post RFQs
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-slate-400 mb-2">
            <span>Step {currentStep} of 4</span>
            <span>{Math.round(getStepProgress())}% Complete</span>
          </div>
          <Progress value={getStepProgress()} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {['Basic Info', 'Business ID', 'Video', 'Intent'].map((step, idx) => (
              <div key={idx} className={`flex flex-col items-center ${idx + 1 <= currentStep ? 'text-blue-400' : 'text-slate-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 
                  ${idx + 1 < currentStep ? 'bg-blue-500 border-blue-500 text-white' : 
                    idx + 1 === currentStep ? 'border-blue-500 text-blue-400' : 'border-slate-600'}`}>
                  {idx + 1 < currentStep ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                </div>
                <span className="text-xs mt-1 hidden sm:block">{step}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Universal Fields */}
        {currentStep === 1 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5 text-blue-400" />
                Basic Information
              </CardTitle>
              <CardDescription className="text-slate-400">
                Tell us about you and your company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name *</Label>
                <Input
                  placeholder="John Smith"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-slate-300">Business Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white pl-10"
                  />
                </div>
                {renderEmailBadge()}
                {emailCheckResult?.email_type === 'free' && (
                  <p className="text-sm text-amber-400">{emailCheckResult.message}</p>
                )}
              </div>

              {/* Country & City */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Country *</Label>
                  <Select value={formData.country_code} onValueChange={(v) => handleInputChange('country_code', v)}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {countries.map(c => (
                        <SelectItem key={c.code} value={c.code} className="text-white hover:bg-slate-700">
                          {c.flag} {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">City *</Label>
                  <Input
                    placeholder="London"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div className="space-y-2">
                <Label className="text-slate-300">Company Legal Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="Acme Trading Ltd"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white pl-10"
                  />
                </div>
              </div>

              {/* Company Website */}
              <div className="space-y-2">
                <Label className="text-slate-300">Company Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="https://company.com"
                    value={formData.company_website}
                    onChange={(e) => handleInputChange('company_website', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white pl-10"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className="text-slate-300">Phone (with country code) *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="+44 7912 345678"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white pl-10"
                  />
                </div>
              </div>

              {/* LinkedIn URLs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">LinkedIn Profile URL</Label>
                  <div className="relative">
                    <Linkedin className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                    <Input
                      placeholder="linkedin.com/in/yourname"
                      value={formData.linkedin_profile_url}
                      onChange={(e) => handleInputChange('linkedin_profile_url', e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">LinkedIn Company Page</Label>
                  <div className="relative">
                    <Linkedin className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                    <Input
                      placeholder="linkedin.com/company/name"
                      value={formData.linkedin_company_url}
                      onChange={(e) => handleInputChange('linkedin_company_url', e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label className="text-slate-300">Your Role *</Label>
                <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {BUYER_ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value} className="text-white hover:bg-slate-700">
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Terms Acceptance */}
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="buyer-terms" 
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked)}
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-blue-600"
                    data-testid="buyer-terms-checkbox"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label htmlFor="buyer-terms" className="text-sm font-medium text-white leading-tight cursor-pointer">
                      I accept the Terms of Use <span className="text-red-400">*</span>
                    </label>
                    <p className="text-xs text-slate-400">
                      By checking this box, I agree to GlobalVyapar&apos;s{' '}
                      <Link to="/terms-of-service" target="_blank" className="text-blue-400 hover:underline">
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy-policy" target="_blank" className="text-blue-400 hover:underline">
                        Privacy Policy
                      </Link>.
                      I understand that GlobalVyapar is a platform connecting buyers and sellers.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={submitStep1}
                disabled={!canProceedStep1 || loading || !termsAccepted}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-4"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Continue to Business Verification
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Country-specific fields (was Step 3) */}
        {currentStep === 2 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-400" />
                Business Verification
              </CardTitle>
              <CardDescription className="text-slate-400">
                Provide your business registration details for {countries.find(c => c.code === formData.country_code)?.name || formData.country_code}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {countryFields.map(field => (
                <div key={field.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">
                      {field.label} {field.required && '*'}
                    </Label>
                    <div className="flex items-center gap-2">
                      {field.verification === 'auto' && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                          Auto-verified
                        </Badge>
                      )}
                      {field.verification === 'semi_auto' && (
                        <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                          Admin verified
                        </Badge>
                      )}
                      {field.help_url && (
                        <a href={field.help_url} target="_blank" rel="noopener noreferrer" 
                           className="text-blue-400 hover:text-blue-300">
                          <HelpCircle className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {field.type === 'dropdown' ? (
                    <Select 
                      value={formData.country_fields[field.id] || ''} 
                      onValueChange={(v) => handleCountryFieldChange(field.id, v)}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                        <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                        {field.options?.map(opt => (
                          <SelectItem key={opt} value={opt} className="text-white hover:bg-slate-700">
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.type === 'file_upload' ? (
                    <div className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center hover:border-slate-500 transition-colors">
                      <Upload className="h-8 w-8 mx-auto text-slate-500 mb-2" />
                      <p className="text-sm text-slate-400">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {field.accepted_formats?.join(', ') || 'PDF, JPG, PNG'}
                      </p>
                      <input 
                        type="file" 
                        className="hidden"
                        accept={field.accepted_formats?.join(',')}
                        onChange={(e) => handleCountryFieldChange(field.id, e.target.files[0]?.name)}
                      />
                    </div>
                  ) : (
                    <Input
                      placeholder={field.tooltip || `Enter ${field.label.toLowerCase()}`}
                      value={formData.country_fields[field.id] || ''}
                      onChange={(e) => handleCountryFieldChange(field.id, e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  )}
                  
                  {field.tooltip && (
                    <p className="text-xs text-slate-500">{field.tooltip}</p>
                  )}
                </div>
              ))}

              {/* Free email additional verification notice */}
              {emailCheckResult?.email_type === 'free' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <h4 className="text-amber-400 font-medium flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Additional Verification Required
                  </h4>
                  <p className="text-sm text-slate-400 mb-3">
                    Since you&apos;re using a free email, please also provide business registration documents.
                    Your application will be reviewed within 24 hours.
                  </p>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={submitStep2}
                  disabled={!canProceedStep2 || loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Video Verification */}
        {currentStep === 3 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Video className="h-5 w-5 text-blue-400" />
                Video ID Verification
              </CardTitle>
              <CardDescription className="text-slate-400">
                Record a short video holding your business ID document
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RegistrationVideoVerification
                countryCode={formData.country_code}
                countryName={countries.find(c => c.code === formData.country_code)?.name}
                businessId={formData.country_fields[countryFields.find(f => f.required)?.id]}
                businessIdLabel={getPrimaryBusinessIdLabel()}
                companyName={formData.company_name}
                fullName={formData.full_name}
                onVideoRecorded={handleVideoRecorded}
              />

              {/* Video Status */}
              {videoBlob && (
                <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  <span className="text-sm text-emerald-300 font-medium">Video recorded successfully!</span>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={submitStep3}
                  disabled={!canProceedStep3}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="step3-next-btn"
                >
                  Next: Buying Intent
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Buying Intent */}
        {currentStep === 4 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Package className="h-5 w-5 text-orange-400" />
                Buying Intent
              </CardTitle>
              <CardDescription className="text-slate-400">
                Help us match you with the right exporters
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Products Interested */}
              <div className="space-y-3">
                <Label className="text-slate-300">Products Interested In *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PRODUCT_CATEGORIES.map(cat => (
                    <div
                      key={cat.id}
                      onClick={() => handleProductToggle(cat.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all text-sm
                        ${formData.products_interested.includes(cat.id) 
                          ? 'bg-orange-500/20 border-orange-500 text-orange-400' 
                          : 'bg-slate-900 border-slate-600 text-slate-400 hover:border-slate-500'}`}
                    >
                      {formData.products_interested.includes(cat.id) && (
                        <CheckCircle className="h-3 w-3 inline mr-1" />
                      )}
                      {cat.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Volume */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Expected Monthly Volume *
                </Label>
                <Select value={formData.monthly_volume} onValueChange={(v) => handleInputChange('monthly_volume', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="Select volume range" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {VOLUME_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Buying Frequency */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Buying Frequency *
                </Label>
                <Select value={formData.buying_frequency} onValueChange={(v) => handleInputChange('buying_frequency', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="How often do you buy?" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Terms */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Preferred Payment Terms *
                </Label>
                <Select value={formData.payment_terms} onValueChange={(v) => handleInputChange('payment_terms', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {PAYMENT_TERMS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Timeline */}
              <div className="space-y-2">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Purchase Timeline *
                </Label>
                <Select value={formData.timeline} onValueChange={(v) => handleInputChange('timeline', v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="When do you need products?" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {TIMELINE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sample Required */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sample"
                  checked={formData.sample_required}
                  onCheckedChange={(checked) => handleInputChange('sample_required', checked)}
                />
                <Label htmlFor="sample" className="text-slate-300 cursor-pointer">
                  I need samples before placing bulk orders
                </Label>
              </div>

              {/* Current Suppliers */}
              <div className="space-y-2">
                <Label className="text-slate-300">Current Suppliers (Optional)</Label>
                <Textarea
                  placeholder="Tell us about your current suppliers, if any..."
                  value={formData.current_suppliers}
                  onChange={(e) => handleInputChange('current_suppliers', e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white"
                  rows={3}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(3)}
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={submitStep4}
                  disabled={!canProceedStep4 || loading}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="complete-verification-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Complete Verification
                  <CheckCircle className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5/6: Success/Pending */}
        {(currentStep === 5 || currentStep === 6) && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Application Submitted!
              </h2>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                {emailCheckResult?.email_type === 'free' 
                  ? "Your application is under review. We'll notify you via email within 24 hours."
                  : "Your verification is complete! You can now contact exporters and post RFQs."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Browse Products
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="border-slate-600 text-slate-300"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
